import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { isChatTeammateTurnInFlight } from '@shared';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, from, of, timer, zip } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import * as fromGenerated from '../../_generated';
import { AnalyticsService } from '../../analytics';
import * as fromFeatureFlags from '../../feature-flags';
import { ViewTransitionTracker } from '../../view-transitions';
import {
  LOADING_TIME_FOR_MESSAGE,
  RECENT_CHATS_LIMIT,
} from '../chats.constants';
import { ChatsSocket } from '../chats.socket';
import * as fromShared from '../shared';
import { ChatCreationService } from './chat-creation.service';
import { isDraft } from './chat-lifecycle.helper';
import {
  ChatActions,
  ChatsActions,
  ChatsSocketActions,
  ChatWithMessagesActions,
} from './chats.actions';
import { chatsFeature, ChatsState } from './chats.reducer';
import {
  chatMessageRefusalKey,
  isRenameRequestCurrent,
  isServerRefusal,
  readChatDocumentRejection,
} from './chats.store.helper';

const DOCUMENT_CITATIONS_LOADING_TIMEOUT_MS = 20000;

@Injectable()
export class ChatsEffects {
  private readonly actions$ = inject(Actions);
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly documentsService = inject(fromGenerated.DocumentsService);
  private readonly store = inject(Store<ChatsState>);
  private readonly translateService = inject(TranslateService);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly chatsSocket = inject(ChatsSocket);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly toastrService = inject(ToastrService);
  private readonly chatCreation = inject(ChatCreationService);
  private readonly viewTransitions = inject(ViewTransitionTracker);

  loadChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChats),
      exhaustMap(() =>
        this.chatsService.chatsControllerGetChatsV1(RECENT_CHATS_LIMIT).pipe(
          map((chats) => ChatsActions.loadChatsSuccess({ chats })),
          catchError((error: Error) =>
            of(ChatsActions.loadChatsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loadChatsForAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChatsForAssistant),
      exhaustMap(({ assistantUuid }) =>
        this.chatsService
          .chatsControllerGetChatsV1(RECENT_CHATS_LIMIT, assistantUuid)
          .pipe(
            map((chats) =>
              ChatsActions.loadChatsForAssistantSuccess({ chats }),
            ),
            catchError((error: Error) =>
              of(
                ChatsActions.loadChatsForAssistantFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  selectChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.selectChat),
      map(({ uuid }) => {
        // The activate guard read this chat a moment ago: nothing to read
        // again (a cold load made one request, not two).
        if (
          this.store.selectSignal(chatsFeature.selectPreloadedChatUuid)() ===
          uuid
        ) {
          return ChatActions.preloadConsumed();
        }
        const selectedChat = this.store.selectSignal(
          chatsFeature.selectChatByUuid(uuid),
        )();

        return ChatWithMessagesActions.loadChatWithMessages({
          uuid,
          // If the chat has messages, we don't need to display the loading state
          withoutIsLoading:
            selectedChat?.messages && selectedChat?.messages?.length > 0
              ? true
              : false,
        });
      }),
    ),
  );

  loadChatWithMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.loadChatWithMessages),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChat)),
      switchMap(([{ uuid }, chat]) => {
        const requestedAt = Date.now();
        /**
         * A draft has no server row, so there is nothing to load, and a fetch
         * would answer 404 and send the person to /not-found (SB-3944).
         */
        if (chat && isDraft(chat)) {
          return of(
            ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages: { ...chat, messages: chat.messages ?? [] },
              requestedAt,
            }),
          );
        }

        return zip(
          this.chatsService.chatsControllerGetChatWithMessagesV1(uuid),
          timer(LOADING_TIME_FOR_MESSAGE),
        ).pipe(
          map(([chatWithMessages]) => {
            return ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages,
              requestedAt,
            });
          }),
          catchError((error: Error) =>
            of(
              ChatWithMessagesActions.loadChatWithMessagesFailure({
                error: error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  loadChatWithMessagesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.loadChatWithMessagesFailure),
        switchMap(() => from(this.routerService.navigateToNotFound())),
      ),
    { dispatch: false },
  );

  addChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.addChatMessage),
      // SB-3944 / KD-54: one stream per chat. A send in chat B never cancels
      // a send in flight in chat A (a first send is two calls since B1); a
      // second send in the same chat still replaces the first, as before.
      groupBy(({ chatUuid }) => chatUuid),
      mergeMap((sends$) =>
        sends$.pipe(
          switchMap(({ chatUuid, content, documents }) => {
            // The skill is set once, when the chat is created (SB-3944 / KD-4),
            // so the message never carries `assistantUuid` or `assistantLocale`.
            const addChatMessageDto: fromGenerated.AddChatMessageDto = {
              content,
            };

            if (documents) {
              addChatMessageDto.documentIds = documents.map(
                (document) => document.uuid,
              );
            }

            // A new chat is created first (KD-15); the message then goes to the
            // uuid the create settled on, which a 409 may have changed.
            let chatExists = false;
            return this.chatCreation.ensureChatCreated(chatUuid).pipe(
              tap(() => {
                chatExists = true;
              }),
              switchMap(({ uuid }) =>
                this.chatsService.chatsControllerAddChatMessageV1(
                  uuid,
                  addChatMessageDto,
                ),
              ),
              map((chatWithMessages) =>
                ChatWithMessagesActions.addChatMessageSuccess({
                  chatWithMessages,
                  sentDocumentUuids: addChatMessageDto.documentIds ?? [],
                }),
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              catchError((error: any) => {
                // SB-3538 T3: the empty-send refusals get their own copy, a
                // refused create gets CHAT_CREATE_REFUSED, and every other
                // failure gets the generic copy. The toast renders its string
                // raw, so an API error name never reaches it (SB-3944 B1 QA).
                const refusalKey =
                  chatMessageRefusalKey(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    error?.error?.name,
                  ) ?? 'TOAST.ERROR.DEFAULT';
                return of(
                  ChatWithMessagesActions.addChatMessageFailure({
                    chatUuid: this.chatCreation.latestUuid(chatUuid),
                    error: this.translateService.instant(refusalKey) as string,
                    // SB-3944 / KD-41: a failed `POST /chats` wrote neither the
                    // chat nor the message, whatever its status, so it always
                    // rolls back. Only the message's own 5xx may follow a write.
                    refused: !chatExists || isServerRefusal(error),
                  }),
                );
              }),
            );
          }),
        ),
      ),
    ),
  );

  /**
   * SB-3978 B2-T38: the server accepted a Teammate chat's FIRST message, so
   * move to its conversation page. The epic's "navigate only after the server
   * accepts": a refused first send stays on the Teammate page (plan Decision
   * 4). "First" is read on the server's copy, one user message, so a later
   * send never pulls the person back to this chat. Nothing happens when the
   * address already names the chat (a draft sent from the conversation page).
   * Its stream is keyed by the chat's uuid, which the navigation keeps, so
   * the frames that came before it still show. Nothing happens either once
   * the person has moved to another chat or Teammate while the two calls ran
   * (#1923): the success must not pull them back.
   */
  navigateAfterTeammateStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.addChatMessageSuccess),
        withLatestFrom(this.store.select(chatsFeature.selectSelectedChatUuid)),
        filter(
          ([{ chatWithMessages: chat }, selectedChatUuid]) =>
            !!chat.teammateId &&
            this.isTeammateStartToOpen(chat, selectedChatUuid),
        ),
        switchMap(([{ chatWithMessages: chat }]) =>
          from(this.routerService.navigateToChat(chat.uuid)),
        ),
      ),
    { dispatch: false },
  );

  /**
   * SB-3978 B2-T38 (PR #2015 review): a Teammate chat's FIRST send that the
   * server took the chat for but then failed without refusing (a 5xx, a
   * timeout, a dropped connection on the message POST) keeps its message,
   * marked failed, as main's general first send does. The Teammate page shows
   * only refusals and no messages, so it would strand the person there with a
   * locked composer; their conversation page shows the message and its error.
   * Same guards as the success: first send, still the selected chat, not
   * already addressed.
   */
  navigateAfterFailedTeammateStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.addChatMessageFailure),
        filter(({ refused }) => !refused),
        withLatestFrom(
          this.store.select(chatsFeature.selectEntities),
          this.store.select(chatsFeature.selectSelectedChatUuid),
        ),
        switchMap(([{ chatUuid }, entities, selectedChatUuid]) => {
          const chat = entities[chatUuid];
          return chat?.teammateId &&
            this.isTeammateStartToOpen(chat, selectedChatUuid)
            ? from(this.routerService.navigateToChat(chat.uuid))
            : EMPTY;
        }),
      ),
    { dispatch: false },
  );

  /**
   * Whether a Teammate chat's send should open its conversation: it is the
   * chat's first user message, the chat is still the selected one, and the
   * address doesn't already name it.
   */
  private isTeammateStartToOpen(
    chat: {
      uuid: string;
      messages?: readonly { isFromUser: boolean }[] | undefined;
    },
    selectedChatUuid: string | null | undefined,
  ): boolean {
    return (
      (chat.messages ?? []).filter((message) => message.isFromUser).length ===
        1 &&
      selectedChatUuid === chat.uuid &&
      this.routerService.addressedChatUuid() !== chat.uuid
    );
  }

  /**
   * SB-3978 B3-T15 (Q7): a quick action refused because the chat's Teammate
   * turn is still running gets translated copy. Every other quick-action
   * failure keeps today's behavior (no toast).
   */
  requestQuickActionTurnInFlightToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.requestQuickActionFailure),
        filter(({ name }) => isChatTeammateTurnInFlight(name)),
        tap(() =>
          this.toastrService.error(
            this.translateService.instant(
              'TOAST.ERROR.TEAMMATE_TURN_IN_FLIGHT',
            ) as string,
          ),
        ),
      ),
    { dispatch: false },
  );

  requestDocumentCitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.requestDocumentCitations),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChatUuid)),
      filter(([_, selectedChatUuid]) => !!selectedChatUuid),
      switchMap(([{ chatMessageId, documentUuid }, selectedChatUuid]) => {
        return this.chatsService
          .chatsControllerGenerateDocumentCitationsV1(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedChatUuid!,
            chatMessageId,
            documentUuid,
          )
          .pipe(map(() => ({ chatMessageId, documentUuid, selectedChatUuid })));
      }),
      // Cancel other timers when clicking on another chip
      switchMap(({ chatMessageId, documentUuid, selectedChatUuid }) =>
        timer(DOCUMENT_CITATIONS_LOADING_TIMEOUT_MS).pipe(
          withLatestFrom(
            this.store.select(chatsFeature.selectIsCitationsLoading),
          ),
          map(([, isCitationsLoading]) => {
            if (isCitationsLoading) {
              Sentry.captureEvent({
                message: 'Document citations request timed out',
                level: 'error',
                extra: {
                  chatMessageId,
                  documentUuid,
                  selectedChatUuid,
                },
              });
            }
            return ChatWithMessagesActions.setCitationsLoading({
              isLoading: false,
            });
          }),
        ),
      ),
      catchError((error: Error) =>
        of(
          ChatWithMessagesActions.requestDocumentCitationsFailure({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  requestQuickAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.requestQuickAction),
      // SB-3944 / KD-54: one chat's quick action must not cancel another's.
      mergeMap(({ chatUuid, chatMessageId, quickAction, quickActionMessage }) =>
        this.chatsService
          .chatsControllerRequestQuickActionV1(chatUuid, chatMessageId, {
            quickAction,
            quickActionMessage,
          })
          .pipe(
            map(() =>
              ChatWithMessagesActions.requestQuickActionSuccess({
                chatUuid,
                quickAction,
              }),
            ),
            catchError((error: Error & { error?: { name?: unknown } }) =>
              of(
                ChatWithMessagesActions.requestQuickActionFailure({
                  chatUuid,
                  error: error.message,
                  quickActionMessage,
                  // SB-3978 B3-T15: the API's refusal name, when it sent one.
                  name:
                    typeof error.error?.name === 'string'
                      ? error.error.name
                      : undefined,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.deleteChat),
      switchMap(
        ({
          uuid,
          conversationId,
          assistantId,
          startedAsDefaultChat,
          teammateId,
        }) =>
          this.chatsService.chatsControllerSoftDeleteChat0V1(uuid).pipe(
            map(() =>
              ChatsActions.deleteChatSuccess({
                uuid,
                conversationId,
                assistantId,
                startedAsDefaultChat,
                teammateId,
              }),
            ),
            catchError((error: Error) =>
              of(ChatsActions.deleteChatFailure({ error: error.message })),
            ),
          ),
      ),
    ),
  );

  setChatConnectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.setChatConnectors),
      // Preference writes are ordered; send remains disabled until they have settled.
      concatMap(({ uuid, disabledConnectorIds }) => {
        // A draft has no row to patch: the exclusions stay in the store, and
        // `ChatCreationService` sends them once the chat is created (KD-15).
        const chat = this.store.selectSignal(
          chatsFeature.selectChatByUuid(uuid),
        )();
        if (isDraft(chat)) {
          return of(
            ChatsActions.setChatConnectorsSuccess({
              uuid,
              disabledConnectorIds,
            }),
          );
        }

        return this.chatsService
          .chatsControllerUpdateChatConnectorsV1(uuid, {
            disabledConnectorIds,
          })
          .pipe(
            // A write that never answers would hold send disabled, and every later write queued
            // behind it, until a reload. Give up and take the failure path, which unlocks both.
            timeout(10_000),
            map(() =>
              ChatsActions.setChatConnectorsSuccess({
                uuid,
                disabledConnectorIds,
              }),
            ),
            catchError((error: Error) =>
              of(
                ChatsActions.setChatConnectorsFailure({
                  uuid,
                  error: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  setChatConnectorsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.setChatConnectorsFailure),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'PROMPT_INPUT.PLUS_MENU.UPDATE_FAILED',
            ) as string,
          );
        }),
      ),
    { dispatch: false },
  );

  renameChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.renameChat),
      mergeMap(({ uuid, title, previousTitle, requestId, source }) =>
        this.chatsService.chatsControllerRenameChatV1(uuid, { title }).pipe(
          map(() =>
            ChatsActions.renameChatSuccess({
              uuid,
              title,
              requestId,
              source,
            }),
          ),
          catchError((error: Error) =>
            // Sampled before the failure action dispatches: a newer rename of
            // the same chat overwrites the active requestId, so a mismatch
            // means this failure is stale (the reducer ignores it too).
            this.store.select(chatsFeature.selectActiveRenameRequests).pipe(
              take(1),
              map((activeRenameRequests) =>
                ChatsActions.renameChatFailure({
                  uuid,
                  title,
                  previousTitle,
                  requestId,
                  error: error.message,
                  superseded: !isRenameRequestCurrent(
                    activeRenameRequests,
                    uuid,
                    requestId,
                  ),
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  renameChatFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.renameChatFailure),
        filter(({ superseded }) => !superseded),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  sendConversationRenamedAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.renameChatSuccess),
        mergeMap(({ uuid, source, title }) => {
          // The Mixpanel conversationId is the numeric Chat.id (SB-3944 /
          // KD-28); the entity is resolved when the success lands, and a chat
          // that is gone by then sends no event.
          const chat = this.store.selectSignal(
            chatsFeature.selectChatByUuid(uuid),
          )();

          if (!chat) {
            return EMPTY;
          }

          return this.analyticsService.sendConversationRenamedEvent({
            conversationId: chat.id,
            source,
            titleLength: title.length,
          });
        }),
      ),
    { dispatch: false },
  );

  sendConversationDeletedAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.deleteChatSuccess),
        mergeMap(
          ({
            conversationId,
            assistantId,
            startedAsDefaultChat,
            teammateId,
          }) => {
            // Snapshot taken at dispatch: the reducer removes the entity on
            // `deleteChat`, so the numeric id is unreadable by success time.
            if (conversationId === undefined) {
              return EMPTY;
            }

            return this.analyticsService.sendConversationDeletedEvent({
              conversationId,
              ...(assistantId && { assistantId }),
              ...(startedAsDefaultChat && { startedAsDefaultChat: true }),
              ...(teammateId && { teammateId }),
            });
          },
        ),
      ),
    { dispatch: false },
  );

  chatsSocketConnect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsSocketActions.reconnect),
        tap(() => {
          this.chatsSocket.reconnect();
        }),
      ),
    { dispatch: false },
  );

  addDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.addDocuments),
      mergeMap(({ transactionId, chatUuid, files, documentRole }) => {
        const documentUuids = files.map((file) => file.uuid);

        // The first file of a new chat creates it (SB-3944 / KD-15). Files
        // dropped together share one create. The API reads the skill of the
        // chat itself (KD-45), so the request carries none.
        return this.chatCreation.ensureChatCreated(chatUuid).pipe(
          switchMap(({ uuid }) => {
            const payload: fromGenerated.CreateDocumentFromChatDto = {
              transactionId,
              chatUuid: uuid,
              documentsToCreate: files.map((file) => ({
                documentId: file.uuid,
                filename: file.name,
                ...(documentRole ? { role: documentRole } : {}),
              })),
            };

            return this.documentsService.documentsControllerCreateDocumentsFromChatV1(
              payload,
            );
          }),
          map((documents) =>
            ChatWithMessagesActions.addDocumentsSuccess({
              chatUuid: this.chatCreation.latestUuid(chatUuid),
              documents,
            }),
          ),
          catchError((error: Error) => {
            // A `from-chat` 422 may name WHY it refused a document (a
            // `.jpg` renamed `.txt` never reaches ingestion, so this is the
            // only channel that cause has - SB-3515 section 4). Spread, not
            // assigned: `exactOptionalPropertyTypes`, and most failures
            // carry no reason at all.
            const rejection = readChatDocumentRejection(error);
            return of(
              ChatWithMessagesActions.addDocumentsFailure({
                chatUuid: this.chatCreation.latestUuid(chatUuid),
                documentUuids,
                error: error.message,
                ...(rejection && { rejection }),
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * The address follows a chat once it is created, and a draft a 409 moved to
   * a new uuid (SB-3944 / KD-18, KD-26, KD-59): a created chat drops the skill
   * hint, a re-keyed draft keeps it, and Back never returns to a refused uuid
   * or a stale hint (KD-48).
   *
   * - It waits for the send's view transition to end: a navigation starts its
   *   own, which would skip the send's (KD-30). After a first file no
   *   transition runs, and the replace follows at once.
   * - One stream per chat: a 409 and its retry share the new uuid, so the
   *   retry's create supersedes the 409's pending replace; another chat's
   *   create never cancels this one's.
   * - It reads the address and the chat when it runs, not when the action
   *   came: the person may have left (the selection outlives the conversation
   *   route), and `latestUuid` matches the refused uuid a 409 left in the URL.
   *
   * The uuid does not change on a create, so the guard finds the entity and
   * the router effect selects nothing.
   */
  replaceAddressAfterCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.chatUuidReassigned, ChatActions.createChatSuccess),
        map((action) =>
          action.type === ChatActions.chatUuidReassigned.type
            ? action.toUuid
            : action.chatWithMessages.uuid,
        ),
        groupBy((chatUuid) => chatUuid),
        mergeMap((creates$) =>
          creates$.pipe(
            switchMap((chatUuid) =>
              from(this.viewTransitions.settled()).pipe(map(() => chatUuid)),
            ),
            switchMap((chatUuid) => {
              const addressed = this.routerService.addressedChatUuid();
              if (
                !addressed ||
                this.chatCreation.latestUuid(addressed) !== chatUuid
              ) {
                return EMPTY;
              }
              const chat = this.store.selectSignal(
                chatsFeature.selectChatByUuid(chatUuid),
              )();
              const hint = isDraft(chat) ? (chat?.assistantId ?? null) : null;
              return from(
                this.routerService.replaceChatAddress(chatUuid, hint),
              );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  sendMessageSentAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.addChatMessageSuccess),
        // SB-3944 / KD-54: each send's event posts on its own; another chat's
        // send must not cancel it.
        mergeMap(({ chatWithMessages }) =>
          this.analyticsService.sendMessageSentEvent(chatWithMessages),
        ),
      ),
    { dispatch: false },
  );

  /**
   * SB-3651. One event per streamed answer, fired off the SAME action that
   * ends the stream: reducers run before effects, so `completedStreams`
   * already holds this turn's record by the time this reads it. Its `streamed`
   * field keeps a turn that landed whole from reporting itself as streamed.
   */
  sendAnswerStreamedAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess,
        ),
        // SB-3944 / KD-54: two chats can finish close together; the second
        // must not cancel the first one's POST.
        mergeMap(({ chatWithMessages }) => {
          const completed = this.store.selectSignal(
            chatsFeature.selectCompletedStreams,
          )()[chatWithMessages.uuid];

          const shownLive = this.store.selectSignal(
            fromFeatureFlags.featureFlagsFeature.selectThinkingStreaming,
          )();

          // Only a reply core-ai streamed AND the user watched stream (the
          // flag off shows the reply whole). Named fields only, never
          // `...completed`: no step text, query or answer may ride analytics
          // (Article XX).
          return shownLive && completed?.streamed
            ? this.analyticsService.sendAnswerStreamedEvent({
                chatId: chatWithMessages.id,
                durationMs: completed.durationMs,
                stepsCount: completed.stepsCount,
                hadWebSearch: completed.hadWebSearch,
              })
            : of(null);
        }),
      ),
    { dispatch: false },
  );

  sendQuickActionSentAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.requestQuickActionSuccess),
        // SB-3944 / KD-54: one chat's event must not cancel another's.
        mergeMap(({ chatUuid, quickAction }) => {
          const selectedChat = this.store.selectSignal(
            chatsFeature.selectChatByUuid(chatUuid),
          )();

          if (!selectedChat) {
            return of(null);
          }

          return this.analyticsService.sendQuickActionSentEvent(
            {
              ...selectedChat,
              messages: selectedChat.messages ?? [],
            },
            quickAction,
          );
        }),
      ),
    { dispatch: false },
  );
}
